import { CircleCheck, Radio } from "lucide-react";
import { linePath, smoothPoints, type Pt } from "features/onboarding/art/chart";
import {
  Art,
  ArtSwitch,
  Card,
  Chip,
  Head,
  Line,
  Marker,
  Status,
  Toast,
  Window,
} from "features/onboarding/art/live";
import {
  TXT,
  fade,
  follow,
  font,
  pop,
  r2,
  ride,
  rise,
  say,
  soft,
  tone,
  useArtId,
  type Box,
  type Scene,
} from "features/onboarding/art/kit";
import useArtLabels from "features/onboarding/art/labels";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";

// Ported from cornix.io (feature/art/signals-bots.tsx): a signal posted in a
// channel reaches the signals bot, which buys in the entry zone and takes
// profit at each target.

const HI = 66500;
const SPAN = 2900;
const v = (p: number) => (HI - p) / SPAN;
const ENTRY: [number, number] = [64300, 64700];
const TARGETS = [65300, 65800, 66300];
const STOP = 63800;

// Dips into the entry zone (the bot buys), then climbs through all three targets.
const KEYS: Pt[] = [
  [0, 0.47],
  [0.35, 0.73],
  [0.6, 0.4],
  [1, v(TARGETS[2])],
];

type Geo = {
  name: string;
  w: number;
  h: number;
  card: Box;
  rows: [number, number];
  link: [Pt, Pt];
  win: Box;
  chart: Box;
  band: Box;
  tagX: number;
  toast: { x: number; y: number } | null;
};

const WIDE: Geo = {
  name: "w",
  w: 640,
  h: 440,
  card: { x: 14, y: 70, w: 250, h: 200 },
  rows: [114, 22],
  link: [
    [264, 170],
    [290, 170],
  ],
  win: { x: 290, y: 24, w: 336, h: 360 },
  chart: { x: 306, y: 76, w: 304, h: 292 },
  band: { x: 320, y: 124, w: 206, h: 226 },
  tagX: 538,
  toast: { x: 346, y: 358 },
};

const TALL: Geo = {
  name: "t",
  w: 360,
  h: 496,
  card: { x: 20, y: 12, w: 320, h: 176 },
  rows: [110, 21],
  link: [
    [180, 188],
    [180, 216],
  ],
  win: { x: 8, y: 216, w: 344, h: 270 },
  chart: { x: 22, y: 266, w: 316, h: 206 },
  band: { x: 36, y: 302, w: 212, h: 156 },
  tagX: 262,
  toast: null,
};

export function SignalsBotsArt({
  play,
  avatar,
}: {
  play?: boolean;
  /** The channel's picture, shown on the posted signal. */
  avatar?: string;
}) {
  return (
    <ArtSwitch
      wide={<SignalsBots g={WIDE} play={play} avatar={avatar} />}
      narrow={<SignalsBots g={TALL} play={play} avatar={avatar} />}
    />
  );
}

function SignalsBots({
  g,
  play,
  avatar,
}: {
  g: Geo;
  play?: boolean;
  avatar?: string;
}) {
  const L = useArtLabels();
  const { t } = useTypedTranslation(LanguageNameSpaces.ONBOARDING);
  const words = {
    breakeven: t("visual.signals.sl_be"),
    inTrade: t("visual.signals.status_trade"),
    closed: t("visual.signals.status_closed"),
  };
  const id = useArtId(`signals-${g.name}`);
  const { band, card } = g;
  const Y = (u: number) => r2(band.y + u * band.h);
  const pts = smoothPoints(
    KEYS.map(([u, y]): Pt => [band.x + u * band.w, Y(y)]),
    10
  ).map(([x, y]): Pt => [r2(x), r2(y)]);
  const path = follow(pts);
  const firstAt = (y: number, down: boolean) =>
    pts.findIndex(
      ([, py], i) =>
        i > 0 &&
        (down ? py >= y : py <= y) &&
        (down ? pts[i - 1][1] < y : pts[i - 1][1] > y)
    );
  const fillI = firstAt(Y(v(ENTRY[1])), true);
  const hits = TARGETS.map((p) => {
    const y = Y(v(p));
    const i = pts.findIndex(([, py], k) => k > fillI && py <= y);
    return { y, i: i < 0 ? pts.length - 1 : i };
  });
  const fill = pts[fillI];
  const end = pts[pts.length - 1];

  const START = 1.9;
  const DRAW = 4.2;
  const at = (i: number) => START + path.fractions[i] * DRAW;

  const scene: Scene = (tl, $) => {
    rise(tl, $("signal"), 0.1, 14, 0.7);
    tl.fromTo(
      $("pulse"),
      { opacity: 0 },
      { opacity: 1, duration: 0.2, yoyo: true, repeat: 1 },
      0.5
    );
    const [a, b] = g.link;
    tl.fromTo(
      $("packet"),
      { attr: { cx: a[0], cy: a[1] }, opacity: 1 },
      { attr: { cx: b[0], cy: b[1] }, duration: 0.5, ease: "power2.in" },
      0.9
    );
    tl.to($("packet"), { opacity: 0, duration: 0.15 }, 1.4);
    rise(tl, $("received"), 1.35, 6, 0.5);
    rise(tl, $("levels"), 1.5, 0, 0.6);
    ride(tl, $("price"), $("head")[0], pts, START, DRAW);
    pop(tl, $("buy"), at(fillI));
    rise(tl, $("buy-chip"), at(fillI) + 0.1);
    hits.forEach((h, i) => rise(tl, $(`hit${i}`), at(h.i), 0, 0.3));
    // Target 1: a little profit, and the stop moves up to the entry.
    const t1 = at(hits[0].i);
    rise(tl, $("profit"), t1 + 0.05, 8, 0.5);
    fade(tl, $("profit"), t1 + 1.8, 0.4);
    tl.fromTo(
      $("sl"),
      { y: 0 },
      {
        y: entryY - Y(v(STOP)),
        duration: 0.8,
        ease: "expo.inOut",
        immediateRender: false,
      },
      t1 + 0.3
    );
    fade(tl, $("buy-chip"), t1 + 0.3, 0.3);
    // The stop lands on the entry (breakeven): its tag takes the entry's
    // place rather than sitting on top of it.
    fade(tl, $("entry-level"), t1 + 0.3, 0.3);
    say(tl, $("sl-text")[0], words.breakeven, t1 + 0.7, "SL");
    say(tl, $("status-text")[0], words.inTrade, at(fillI), L.following);
    say(tl, $("status-text")[0], words.closed, START + DRAW, undefined);
    if (g.toast) rise(tl, $("toast"), START + DRAW + 0.2, 12, 0.7);
    else {
      fade(tl, $("received"), START + DRAW, 0.25);
      rise(tl, $("closed"), START + DRAW + 0.2, 6, 0.5);
    }
    const rest = START + DRAW + 1;

    const out = rest + 3.4;
    fade(
      tl,
      [
        ...$("signal"),
        ...$("received"),
        ...$("levels"),
        ...$("price"),
        ...$("head"),
        ...$("buy"),
        ...$("buy-chip"),
        ...$("hit*"),
        ...$(g.toast ? "toast" : "closed"),
      ],
      out
    );
    tl.to({}, { duration: 0.6 }, out + 0.5);
    return rest;
  };

  const x1 = band.x + band.w;
  const tagW = g.chart.x + g.chart.w - 8 - g.tagX;
  const entryY = Y(v((ENTRY[0] + ENTRY[1]) / 2));
  const level = (
    p: number,
    t: "up" | "down" | "accent",
    label: string,
    dash = "5 4"
  ) => {
    const y = Y(v(p));
    return (
      <g key={label}>
        <line
          x1={band.x}
          x2={x1}
          y1={y}
          y2={y}
          stroke={tone(t)}
          strokeOpacity={0.7}
          strokeWidth={1.3}
          strokeDasharray={dash}
        />
        <rect
          x={g.tagX}
          y={y - 10}
          width={tagW}
          height={20}
          rx={6}
          fill={soft(t)}
        />
        <text
          data-k={label === "SL" ? "sl-text" : undefined}
          x={g.tagX + tagW / 2}
          y={y + 3.5}
          textAnchor="middle"
          fill={tone(t)}
          className={TXT.num}
          style={font(10.5, 600)}
        >
          {label}
        </text>
      </g>
    );
  };
  const row = (label: string, value: string, i: number) => (
    <g key={label}>
      <text
        x={card.x + 26}
        y={card.y + g.rows[0] + i * g.rows[1]}
        className={TXT.label}
      >
        {label}
      </text>
      <text
        x={card.x + 76}
        y={card.y + g.rows[0] + i * g.rows[1]}
        className={`${TXT.ink} ${TXT.num}`}
        style={font(11.5, 600)}
      >
        {value}
      </text>
    </g>
  );
  const chipText = L.signalReceived;
  const chipW = Math.max(112, Math.round(chipText.length * 7 + 24));
  const closedText = `${L.closed} · +1.7%`;
  const closedW = Math.max(112, Math.round(closedText.length * 7 + 24));
  const chipX = (w: number) => g.chart.x + g.chart.w - 8 - w / 2;

  return (
    <Art id={id} w={g.w} h={g.h} scene={scene} play={play}>
      {/* The signal, as posted in the channel */}
      <Card
        id={id}
        k="signal"
        x={card.x}
        y={card.y}
        w={card.w}
        h={card.h}
        r={16}
      >
        <rect
          data-k="pulse"
          x={card.x}
          y={card.y}
          width={card.w}
          height={card.h}
          rx={16}
          fill="none"
          stroke={tone("accent")}
          strokeWidth={2}
          opacity={0}
        />
        <circle cx={card.x + 32} cy={card.y + 32} r={16} fill={tone("ink")} />
        {avatar ? (
          <>
            <clipPath id={`${id}-avatar`}>
              <circle cx={card.x + 32} cy={card.y + 32} r={16} />
            </clipPath>
            <image
              href={avatar}
              x={card.x + 16}
              y={card.y + 16}
              width={32}
              height={32}
              preserveAspectRatio="xMidYMid slice"
              clipPath={`url(#${id}-avatar)`}
            />
          </>
        ) : (
          <Radio
            x={card.x + 23}
            y={card.y + 23}
            width={18}
            height={18}
            color="var(--viz-card)"
            strokeWidth={2}
          />
        )}
        <rect
          x={card.x + 58}
          y={card.y + 19}
          width={card.w * 0.42}
          height={10}
          rx={5}
          fill="var(--viz-ink-soft)"
        />
        <text x={card.x + 58} y={card.y + 47} className={TXT.label}>
          Telegram · 12:04
        </text>
        <rect
          x={card.x + 14}
          y={card.y + 62}
          width={card.w - 28}
          height={card.h - 76}
          rx={12}
          fill="var(--viz-accent-faint)"
          stroke="var(--viz-rule-soft)"
        />
        <text
          x={card.x + 26}
          y={card.y + 88}
          className={`${TXT.ink} ${TXT.num}`}
          style={font(13.5, 600)}
        >
          BTC/USDT
        </text>
        <Chip
          x={card.x + 140}
          y={card.y + 83.5}
          text={L.long}
          t="up"
          w={Math.max(54, Math.round(L.long.length * 7 + 22))}
        />
        {row(
          L.entry,
          `${ENTRY[0].toLocaleString("en-US")} – ${ENTRY[1].toLocaleString(
            "en-US"
          )}`,
          0
        )}
        {row(
          "TP",
          TARGETS.map((p) => p.toLocaleString("en-US")).join(" · "),
          1
        )}
        {row("SL", STOP.toLocaleString("en-US"), 2)}
      </Card>

      {/* Hand-off to the bot */}
      <g data-in="0.2">
        <line
          x1={g.link[0][0]}
          y1={g.link[0][1]}
          x2={g.link[1][0]}
          y2={g.link[1][1]}
          stroke="var(--viz-accent-line)"
          strokeWidth={2}
          strokeDasharray="3 4"
        />
      </g>
      <circle
        data-k="packet"
        cx={g.link[1][0]}
        cy={g.link[1][1]}
        r={4.5}
        fill={tone("accent")}
        opacity={0}
      />

      <Window
        id={id}
        x={g.win.x}
        y={g.win.y}
        w={g.win.w}
        h={g.win.h}
        title={L.signalsBot}
      >
        <g data-in="0.1">
          <text x={g.chart.x + 14} y={g.chart.y + 24} className={TXT.label}>
            BTC/USDT · {L.long}
          </text>
        </g>
        {g.toast ? (
          <Chip
            k="received"
            x={chipX(chipW)}
            y={g.chart.y + 20}
            text={chipText}
            t="accent"
            w={chipW}
          />
        ) : (
          <>
            <g data-k="received" opacity={0}>
              <Chip
                x={chipX(chipW)}
                y={g.chart.y + 20}
                text={chipText}
                t="accent"
                w={chipW}
              />
            </g>
            <Chip
              k="closed"
              x={chipX(closedW)}
              y={g.chart.y + 20}
              text={closedText}
              t="up"
              w={closedW}
              solid
            />
          </>
        )}

        {/* The signal's levels, placed as orders */}
        <g data-k="levels">
          <rect
            x={band.x}
            y={Y(v(ENTRY[1]))}
            width={band.w}
            height={Y(v(ENTRY[0])) - Y(v(ENTRY[1]))}
            fill={soft("accent")}
            opacity={0.7}
          />
          <g data-k="entry-level">
            {level((ENTRY[0] + ENTRY[1]) / 2, "accent", L.entry, "0")}
          </g>
          {TARGETS.map((p, i) => level(p, "up", `TP ${i + 1}`))}
          <g data-k="sl">{level(STOP, "down", "SL")}</g>
        </g>
        {hits.map((h, i) => (
          <g key={i} data-k={`hit${i}`}>
            <rect
              x={g.tagX}
              y={h.y - 10}
              width={tagW}
              height={20}
              rx={6}
              fill={tone("up")}
            />
            <text
              x={g.tagX + tagW / 2}
              y={h.y + 3.5}
              textAnchor="middle"
              fill="var(--viz-on-solid)"
              style={font(10.5, 600)}
            >
              TP {i + 1} ✓
            </text>
          </g>
        ))}

        <text
          data-k="profit"
          x={pts[hits[0].i][0]}
          y={hits[0].y - 10}
          textAnchor="middle"
          fill={tone("up")}
          className={TXT.num}
          style={font(11, 600)}
        >
          +$38
        </text>
        <Line k="price" d={linePath(pts)} w={2.2} />
        <Marker k="buy" x={fill[0]} y={fill[1]} t="up" />
        {/* The buy shows on the entry's tag (like a hit target), so no chip
            floats over the price line. */}
        <g data-k="buy-chip">
          <rect
            x={g.tagX}
            y={entryY - 10}
            width={tagW}
            height={20}
            rx={6}
            fill={tone("up")}
          />
          <text
            x={g.tagX + tagW / 2}
            y={entryY + 3.5}
            textAnchor="middle"
            fill="var(--viz-on-solid)"
            style={font(10.5, 600)}
          >
            {L.buy} ✓
          </text>
        </g>
        <Head k="head" x={end[0]} y={end[1]} t="up" />
        <Status
          k="status-text"
          x={g.win.x + g.win.w - 18}
          y={g.win.y + 23}
          text={L.following}
        />
      </Window>
      {g.toast ? (
        <Toast
          id={id}
          k="toast"
          x={g.toast.x}
          y={g.toast.y}
          icon={CircleCheck}
          title={`${L.positionClosed} · +1.7%`}
          meta="TP 3/3 ✓"
          t="up"
        />
      ) : null}
    </Art>
  );
}
